import React, { useEffect, useState } from 'react';
import { Plus, Search, Lock, Unlock, Trash2 } from 'lucide-react';
import api from '../../utils/api';

const ROLE_COLORS = { super_admin: 'bg-purple-100 text-purple-700', admin_imobiliaria: 'bg-blue-100 text-blue-700', corretor: 'bg-stone-100 text-stone-600' };
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('pt-BR') : '—';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'corretor', imobiliaria: '' });
  const [confirm, setConfirm] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/admin/users', { params: { q: q || undefined } }).then(r => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [q]);

  const create = async (e) => {
    e.preventDefault();
    await api.post('/admin/users', form);
    setShowCreate(false);
    setForm({ name: '', email: '', password: '', role: 'corretor', imobiliaria: '' });
    load();
  };

  const toggleBlock = async (u) => {
    if (u.blocked) await api.post(`/admin/users/${u.id}/reativar`);
    else await api.post(`/admin/users/${u.id}/bloquear`);
    load();
  };

  const deleteUser = async (u) => {
    await api.delete(`/admin/users/${u.id}`);
    setConfirm(null);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-stone-900">Usuários</h1>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-[#0f3d2e] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#0a2e22] transition-colors">
          <Plus size={16} /> Novo Usuário
        </button>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nome, email ou imobiliária..."
          className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#0f3d2e]" />
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-xs">
            <tr>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3">E-mail</th>
              <th className="text-left px-4 py-3">Papel</th>
              <th className="text-left px-4 py-3">Imobiliária</th>
              <th className="text-left px-4 py-3">Criado</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">Carregando...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">Nenhum usuário encontrado</td></tr>
            ) : users.map(u => (
              <tr key={u.id} className={`hover:bg-stone-50 ${u.blocked ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-medium text-stone-800">{u.name}</td>
                <td className="px-4 py-3 text-stone-500">{u.email}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ROLE_COLORS[u.role] || 'bg-stone-100 text-stone-600'}`}>{u.role}</span></td>
                <td className="px-4 py-3 text-stone-500 truncate max-w-[160px]">{u.imobiliaria || '—'}</td>
                <td className="px-4 py-3 text-stone-400">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right flex justify-end gap-1">
                  <button onClick={() => toggleBlock(u)} title={u.blocked ? 'Reativar' : 'Bloquear'} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500">
                    {u.blocked ? <Unlock size={14} /> : <Lock size={14} />}
                  </button>
                  {u.role !== 'super_admin' && (
                    <button onClick={() => setConfirm(u)} title="Excluir" className="p-1.5 rounded-md hover:bg-red-50 text-stone-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-stone-100 font-semibold text-stone-800">Novo Usuário</div>
            <form onSubmit={create} className="p-6 space-y-4">
              {[['Nome *','name','text',true],['E-mail *','email','email',true],['Senha *','password','password',true],['Imobiliária','imobiliaria','text',false]].map(([lbl,key,type,req]) => (
                <div key={key}><label className="text-xs font-medium text-stone-600 block mb-1">{lbl}</label>
                  <input type={type} required={req} value={form[key]} onChange={e => setForm(f => ({...f, [key]: e.target.value}))} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0f3d2e]" /></div>
              ))}
              <div><label className="text-xs font-medium text-stone-600 block mb-1">Papel</label>
                <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0f3d2e]">
                  <option value="corretor">Corretor</option>
                  <option value="admin_imobiliaria">Admin Imobiliária</option>
                  <option value="super_admin">Super Admin</option>
                </select></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-stone-200 rounded-lg py-2.5 text-sm font-medium text-stone-600 hover:bg-stone-50">Cancelar</button>
                <button type="submit" className="flex-1 bg-[#0f3d2e] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#0a2e22]">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-xl p-6 text-center">
            <Trash2 size={32} className="text-red-400 mx-auto mb-3" />
            <p className="font-semibold text-stone-800 mb-1">Excluir usuário?</p>
            <p className="text-sm text-stone-500 mb-5">{confirm.email}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirm(null)} className="flex-1 border border-stone-200 rounded-lg py-2.5 text-sm font-medium text-stone-600 hover:bg-stone-50">Cancelar</button>
              <button onClick={() => deleteUser(confirm)} className="flex-1 bg-red-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-red-600">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
